import { useState } from "react";
import { Check, Sparkles, ChevronDown, ChevronUp, StickyNote } from "lucide-react";
import AITerminal from "@/components/AITerminal";
import NoteEditor from "@/components/NoteEditor";

export default function QuestionCard({ question, index, mastered, onToggleMastered, note, onNoteChange }) {
  const [open, setOpen] = useState(false);
  const [showAI, setShowAI] = useState(false);

  const hasNote = Boolean(note);

  return (
    <article
      data-testid={`question-card-${question.id}`}
      className={`group bg-zinc-900/60 border ${
        mastered ? "border-emerald-500/40" : "border-zinc-800"
      } hover:border-zinc-700 transition-colors`}
    >
      <header className="flex items-start gap-4 px-5 py-4">
        <div className="text-xs font-mono text-zinc-600 pt-1 select-none w-10 shrink-0">
          {String(index + 1).padStart(3, "0")}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-2 flex-wrap">
            <span className="text-[10px] uppercase tracking-widest text-sky-400 font-bold">
              {question.sectionShort}
            </span>
            <span className="text-[10px] uppercase tracking-widest text-zinc-600">
              · {question.groupTitle}
            </span>
            {mastered && (
              <span
                data-testid={`mastered-badge-${question.id}`}
                className="ml-1 inline-flex items-center gap-1 bg-emerald-500/10 text-emerald-400 px-2 py-0.5 text-[10px] font-mono border border-emerald-500/20 uppercase tracking-widest"
              >
                <Check className="w-3 h-3" /> Maîtrisée
              </span>
            )}
            {hasNote && (
              <span
                data-testid={`note-badge-${question.id}`}
                className="inline-flex items-center gap-1 bg-amber-500/10 text-amber-400 px-2 py-0.5 text-[10px] font-mono border border-amber-500/20 uppercase tracking-widest"
              >
                <StickyNote className="w-3 h-3" /> Note
              </span>
            )}
            {question.custom && (
              <span
                data-testid={`custom-badge-${question.id}`}
                className="inline-flex items-center gap-1 bg-sky-500/10 text-sky-400 px-2 py-0.5 text-[10px] font-mono border border-sky-500/20 uppercase tracking-widest"
              >
                Import
              </span>
            )}
          </div>
          <h3
            className="font-display text-base md:text-lg text-zinc-100 leading-snug cursor-pointer"
            onClick={() => setOpen((v) => !v)}
            data-testid={`question-title-${question.id}`}
          >
            {question.text}
          </h3>
        </div>
        <button
          data-testid={`expand-${question.id}`}
          onClick={() => setOpen((v) => !v)}
          className="text-zinc-500 hover:text-zinc-200 transition-colors mt-1"
          aria-label="Déplier la question"
        >
          {open ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
        </button>
      </header>

      {open && (
        <div className="border-t border-zinc-800 px-5 py-4 fade-up">
          <div className="flex flex-wrap items-center gap-3">
            <button
              data-testid={`generate-ai-${question.id}`}
              onClick={() => setShowAI(true)}
              disabled={showAI}
              className="inline-flex items-center gap-2 bg-sky-500 hover:bg-sky-400 disabled:opacity-50 disabled:cursor-not-allowed text-zinc-950 font-bold px-4 py-2 text-sm transition-colors uppercase tracking-wider"
            >
              <Sparkles className="w-4 h-4" />
              Générer la réponse IA
            </button>

            <button
              data-testid={`toggle-mastered-${question.id}`}
              onClick={() => onToggleMastered(question.id)}
              className={`inline-flex items-center gap-2 border px-4 py-2 text-sm transition-colors uppercase tracking-wider ${
                mastered
                  ? "bg-emerald-500/10 border-emerald-500/40 text-emerald-400 hover:bg-emerald-500/20"
                  : "bg-zinc-900 border-zinc-700 text-zinc-300 hover:border-zinc-500"
              }`}
            >
              <Check className="w-4 h-4" />
              {mastered ? "Maîtrisée" : "Marquer maîtrisée"}
            </button>
          </div>

          {showAI && (
            <AITerminal
              question={question.text}
              questionId={question.id}
              sectionTitle={question.sectionTitle}
              groupTitle={question.groupTitle}
              note={note}
              context={`${question.sectionTitle} — ${question.groupTitle}`}
              onClose={() => setShowAI(false)}
            />
          )}

          <div className="mt-3">
            <NoteEditor
              questionId={question.id}
              initialText={note}
              onChange={(t) => onNoteChange?.(question.id, t)}
            />
          </div>
        </div>
      )}
    </article>
  );
}
